import type { IssueRecoveryCodeInput, IssueRecoveryCodeResult } from '@citizen-walk/shared-types';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * `issueRecoveryCode` callable wrapper hook (Phase 3 sprint 5 client consumer)。
 *
 * 旧 `RecoveryCodeModal.tsx` + `lib/db.getMeta` の組み合わせを v2 で簡素化:
 *   - localStorage 経由のキャッシュは持たず、毎回 callable で発行 (重要: コードは表示時のみ取得、
 *     ユーザが手元で保管する設計、REQ-4.1.5)
 *   - rate limit 超過 (5 req/min/uid) は HttpsError(`resource-exhausted`) として state.error に
 *
 * 解釈B: コードは membership (地区) 単位で保持されるため、`call(districtId)` で対象地区を指定する。
 */

export type IssueRecoveryCodeState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'pending'; data: null; error: null }
  | { status: 'success'; data: IssueRecoveryCodeResult; error: null }
  | { status: 'error'; data: null; error: Error };

export function useIssueRecoveryCode(): {
  state: IssueRecoveryCodeState;
  call: (districtId: string) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<IssueRecoveryCodeState>({
    status: 'idle',
    data: null,
    error: null,
  });

  const call = useCallback(async (districtId: string) => {
    setState({ status: 'pending', data: null, error: null });
    try {
      const fn = httpsCallable<IssueRecoveryCodeInput, IssueRecoveryCodeResult>(
        functions,
        'issueRecoveryCode',
      );
      const res = await fn({ districtId });
      setState({ status: 'success', data: res.data, error: null });
    } catch (e) {
      // HttpsError は code を含む。rate limit (resource-exhausted) は UI 側で専用文言にできるよう
      // error message を判別可能な形に正規化 (reviewer audit ⚠️ B 反映)。
      const raw = e as { code?: string; message?: string };
      const code = typeof raw.code === 'string' ? raw.code : '';
      let message = raw.message ?? String(e);
      if (code.includes('resource-exhausted')) {
        message = 'リカバリコードの発行回数上限に達しました。1 分後に再試行してください。';
      }
      const err = new Error(message);
      // 元 code を保持して呼出側で判別可能に
      (err as Error & { code?: string }).code = code;
      setState({ status: 'error', data: null, error: err });
    }
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', data: null, error: null });
  }, []);

  return { state, call, reset };
}
